import { SyntheticEvent } from 'react';
import moment from 'moment';
import { cloneDeep, mapValues, isNil } from 'lodash';
import { DATE_FORMATS } from 'utils/dateTime';
import {
  normalizeDaysSinceClinicVisit,
  weekIndices,
} from '../../PatientAdherenceTable/utils';

const DEFAULT_EMPTY_VALUE = '-';

export type IClonedPatientByCsv = Exclude<
  Overwrite<
    IFormattedPatientAdherenceDetails,
    {
      rewardsEarnedLast7Days: Nullable<number>;
      rewardsEarnedTotal: Nullable<number>;
      tosPhiDisclosure: string;
    }
  >,
  'primaryClinician'
>;

type IClonedPatient = Overwrite<
  IClonedPatientByCsv,
  {
    rewardsEarnedLast7Days: number | string;
    rewardsEarnedTotal: number | string;
  }
>;

export const mapHeaderKeyToPatient =
  (headerKeys: string[]) =>
  (patient: IClonedPatientByCsv): [string, number] => {
    const clonedPatient = cloneDeep(patient) as IClonedPatient;
    clonedPatient.birthDate = clonedPatient.birthDate
      ? moment(clonedPatient.birthDate).format(DATE_FORMATS.DD_MMM_YYYY)
      : DEFAULT_EMPTY_VALUE;
    clonedPatient.rxCreatedDate = clonedPatient.rxCreatedDate
      ? moment(clonedPatient.rxCreatedDate).format(DATE_FORMATS.DD_MMM_YYYY)
      : DEFAULT_EMPTY_VALUE;
    clonedPatient.rxStartDate = clonedPatient.rxStartDate
      ? moment(clonedPatient.rxStartDate).format(DATE_FORMATS.DD_MMM_YYYY)
      : clonedPatient.accessCode;
    clonedPatient.rewardsEarnedLast7Days = isNil(
      clonedPatient.rewardsEarnedLast7Days,
    )
      ? DEFAULT_EMPTY_VALUE
      : clonedPatient.rewardsEarnedLast7Days;
    clonedPatient.rewardsEarnedTotal = isNil(clonedPatient.rewardsEarnedTotal)
      ? DEFAULT_EMPTY_VALUE
      : clonedPatient.rewardsEarnedTotal;
    const newPatient: { [key: string]: any } = mapValues(clonedPatient, (v) =>
      v === null ? DEFAULT_EMPTY_VALUE : v,
    );
    return headerKeys.map((key: string) => {
      return key in newPatient ? newPatient[key] : '';
    }) as [string, number];
  };

export const jsonsHeaders = (array: IClonedPatientByCsv[]): string[] => {
  return Array.from(
    array
      .map((json) => Object.keys(json))
      // @ts-ignore
      .reduce((a, b) => new Set([...a, ...b]), []),
  );
};

export const jsons2arrays = (
  patientList: IClonedPatientByCsv[],
  headers: string[],
  fields: string[],
): (string[] | [string, number])[] => {
  const csvHeaders = headers || jsonsHeaders(patientList);
  let headerLabels = csvHeaders;
  let headerKeys = fields || csvHeaders;
  const data = patientList.map(mapHeaderKeyToPatient(headerKeys));
  return [headerLabels, ...data];
};

const joiner = (
  data: (string[] | [string, number])[],
  separator: string = ',',
): any =>
  data
    .map(
      // @ts-ignore
      (row) => row.map((element) => `"${element}"`).join(separator),
    )
    .join('\n');

const jsons2csv = (
  patientList: IClonedPatientByCsv[],
  headers: string[],
  fields: string[],
  separator: string,
): string => {
  return joiner(jsons2arrays(patientList, headers, fields), separator);
};

const csvGenerator = (
  patientList: IClonedPatientByCsv[],
  headers: string[],
  fields: string[],
  separator: string = ',',
): string => {
  if (patientList) {
    const result = jsons2csv(patientList, headers, fields, separator);
    return result;
  }

  return '';
};

export const buildURI = (
  patientList: IClonedPatientByCsv[],
  headers: string[],
  fields: string[],
  uFEFF: boolean = false,
  separator: string = ',',
): string => {
  const csvDataURL = encodeURIComponent(
    `${uFEFF ? '\uFEFF' : ''}${csvGenerator(
      patientList,
      headers,
      fields,
      separator,
    )}`,
  );
  return `data:text/csv;charset=utf-8,${csvDataURL}`;
};

export const getFileName = () => {
  const datestamp = moment().format('DDMMMYYYY');
  return `patient_adherence_all_${datestamp}.csv`;
};

export const getFieldNames = (): string[] => {
  const weeks: string[] = weekIndices().map(
    (weekIndex) => `week${weekIndex + 1}`,
  );
  return [
    'lastName',
    'firstName',
    'birthDate',
    'email',
    'clinicName',
    'healthCareProvider',
    'externalRef',
    'rxCreatedDate',
    'rxStartDate',
    'tosPhiDisclosure',
    'rxDaysRemaining',
    ...weeks,
    'totalLessonsCompleted',
    'daysSinceClinicVisit',
    'rewardsEarnedLast7Days',
    'rewardsEarnedTotal',
  ];
};

export const getHeaderColumns = (): string[] => {
  const weeks: string[] = weekIndices().map(
    (weekIndex) => `Week ${weekIndex + 1}`,
  );
  return [
    'Last Name',
    'First Name',
    'Date of Birth',
    'Email',
    'Clinic',
    'Health Care Provider',
    'External Patient ID',
    'Rx Created Date',
    'Rx Start Date',
    'TOS / PHI Disclosure',
    'Rx Days Remaining',
    ...weeks,
    'Total Lessons Completed',
    'Days Since Clinic Visit',
    'Rewards Earned (Last 7 Days)',
    'Rewards Earned (Total)',
  ];
};

export const downloadCsv = (
  evt: SyntheticEvent<HTMLAnchorElement>,
  data: IClonedPatientByCsv[],
  filename: string,
  headers: string[],
  fields: string[],
  separator: string = ',',
): boolean => {
  // @ts-ignore
  // ? this is a deprecated method.  Why test for conditional?
  // generates a ts error as function is always defined
  if (window.navigator.msSaveOrOpenBlob) {
    evt.preventDefault();

    let blob = new Blob([csvGenerator(data, headers, fields, separator)]);
    window.navigator.msSaveBlob(blob, filename);

    return false;
  }

  const element = document.createElement('a');
  element.setAttribute('href', buildURI(data, headers, fields));
  element.setAttribute('download', filename);
  element.click();
  return true;
};

const formatPhiDisclosure = (phiDisclosure: Nullable<boolean>) => {
  if (phiDisclosure == null) return DEFAULT_EMPTY_VALUE;
  return phiDisclosure ? 'Yes' : 'No';
};

export const formatTosPhiDisclosure = (
  phiDisclosure: Nullable<boolean>,
  tosVersionAccepted: Nullable<string>,
): string => {
  const tosValue = tosVersionAccepted ? 'Yes' : DEFAULT_EMPTY_VALUE;
  let phiValue = formatPhiDisclosure(phiDisclosure);
  return `${tosValue} / ${phiValue}`;
};

export const formatCsv = (
  csvData: IFormattedPatientAdherenceDetails[],
): IClonedPatientByCsv[] =>
  csvData.map((unformattedRow: IFormattedPatientAdherenceDetails) => {
    const row = cloneDeep(unformattedRow) as IClonedPatientByCsv;
    row.birthDate = row.birthDate ? row.birthDate : '';
    row.rxStartDate = row.rxStartDate ? row.rxStartDate : null;
    row.externalRef = row.externalRef ? row.externalRef : null;
    row.healthCareProvider = row.primaryHealthCareProvider;
    row.rewardsEarnedLast7Days = row.inAppCm
      ? row.rewardsEarnedLast7Days
      : null;
    row.rewardsEarnedTotal = row.inAppCm ? row.rewardsEarnedTotal : null;
    row.daysSinceClinicVisit = normalizeDaysSinceClinicVisit(
      row.daysSinceClinicVisit,
    );
    row.tosPhiDisclosure = formatTosPhiDisclosure(
      row.phiDisclosure,
      row.tosVersionAccepted,
    );
    delete row.primaryClinician;
    return row;
  });

export const renameSortPropsForCsv = (sortProp: Nullable<string>) => {
  const sortProps = ['days_since_clinic_visit'];
  if (sortProp == null) return '';
  if (sortProps.includes(sortProp)) return `csv_${sortProp}`;

  return sortProp;
};
